import { clientVersion } from '@trello/config';
import { asString, isBrowserSupported } from '@trello/browser';
import { memberId } from '@trello/session-cookie';
import { withScope, captureException } from '@sentry/browser';
import { NetworkError } from '@trello/graphql-error-handling';

interface LegacyNavigator extends Navigator {
  browserLanguage: string;
}

interface SentryEntry {
  name: string;
  msg: string;
  stack?: string;
  ua: string;
  browser: string;
  isBrowserSupported: boolean;
  language: string;
  clientVersion: { head: string; version: number; patch: number };
  tags: {
    [key: string]: boolean | string;
  };
  extra: {
    [key: string]: boolean | string;
  };
  sentryEventId: string | null;
  idMember?: string;
}

interface SentryOwnershipTags {
  ownershipArea?:
    | 'trello-aaaa'
    | 'trello-ahaa'
    | 'trello-billing-platform'
    | 'trello-bizteam'
    | 'trello-data-eng'
    | 'trello-ecosystem'
    | 'trello-enterprise'
    | 'trello-fe-platform'
    | 'trello-nusku'
    | 'trello-remarkable'
    | 'trello-teamplates'
    | 'trello-workflowers';
}

interface SentryTags {
  [key: string]: string;
}

interface SentryExtraData {
  [key: string]: string | boolean;
}

/**
 * SentryErrorMetadata defines additional error context
 * sent to Sentry. Tags are searchable entities, whereas
 * extraData is read-only data that can be viewed on the
 * error page.
 */
export interface SentryErrorMetadata {
  tags?: SentryOwnershipTags & SentryTags;
  extraData?: SentryExtraData;
  networkError?: NetworkError | null;
}

/**
 * Sends an error entry to Sentry
 *
 * @param errorEvent the errorEvent to send to Sentry
 */
export const sendErrorEvent = (
  error: Error,
  metadata: SentryErrorMetadata = {
    tags: {},
    extraData: {},
  },
): string | null => {
  const versionParts = clientVersion.split('-');
  const head = versionParts.slice(0, -1).join('-');
  const version = parseInt(versionParts.slice(-1)[0], 10);
  const memberProperty = memberId ? { idMember: memberId } : {};
  let formattedNetworkError = {};
  if (metadata.networkError) {
    formattedNetworkError = {
      networkErrorCode: metadata.networkError?.code,
      networkErrorMessage: metadata.networkError?.message,
      networkErrorStatus: `${metadata.networkError?.status}`,
    };
  }

  const extra = {
    ...formattedNetworkError,
    ...metadata.extraData,
  };

  let sentryEventId: string | null = null;
  // Send the error to the Atlassian internal sentry
  withScope((scope) => {
    if (metadata.tags) {
      scope.setTags(metadata.tags);
    }
    if (extra) {
      scope.setExtras(extra);
    }
    sentryEventId = captureException(error);
  });

  // Send to the /err endpoint
  const entry: SentryEntry = {
    name: error.name,
    msg: error.message,
    stack: error.stack,
    ua: navigator.userAgent,
    browser: asString,
    isBrowserSupported: isBrowserSupported(),
    language:
      navigator.language || (navigator as LegacyNavigator).browserLanguage,
    clientVersion: { head, version, patch: 0 },
    tags: {
      isBrowserSupported: isBrowserSupported(),
      ...(metadata.tags as SentryTags),
    },
    extra,
    sentryEventId,
    // Only append the idMember property if there's a memberId.
    ...memberProperty,
  };

  if (__DEV__) {
    console.error(entry);
  }

  fetch('/err', {
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    credentials: 'include',
    method: 'POST',
    body: JSON.stringify({ errors: [entry] }),
  }).catch((err) => {
    throw new Error(
      `There was a problem while logging an error: ${err.message}`,
    );
  });

  return sentryEventId;
};
